(() => {
  const ROOT_SELECTOR = '[data-db-support-article]';

  const slugify = (value = '') =>
    String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');

  class DBSupportArticle {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector('[data-support-article-content]');
      this.toc = root.querySelector('[data-support-toc]');
      this.tocList = root.querySelector('[data-support-toc-list]');
      this.headingLinks = new Map();
      this.observer = null;

      if (!this.content) return;

      this.buildTableOfContents();
      this.bindHelpfulButtons();
    }

    buildTableOfContents() {
      if (!this.toc || !this.tocList) return;

      const headings = [...this.content.querySelectorAll('h2, h3')].filter(
        (heading) => heading.textContent.trim().length > 0
      );

      if (headings.length < 2) return;

      const usedIds = new Set();
      const fragment = document.createDocumentFragment();

      headings.forEach((heading, index) => {
        let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
        let uniqueId = id;
        let suffix = 2;

        while (usedIds.has(uniqueId) || (document.getElementById(uniqueId) && document.getElementById(uniqueId) !== heading)) {
          uniqueId = `${id}-${suffix}`;
          suffix += 1;
        }

        heading.id = uniqueId;
        usedIds.add(uniqueId);

        const listItem = document.createElement('li');
        if (heading.tagName === 'H3') listItem.className = 'is-subheading';

        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(uniqueId)}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const url = new URL(window.location.href);
          url.hash = uniqueId;
          window.history.pushState({}, '', url);
          heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        this.headingLinks.set(heading, link);
        listItem.append(link);
        fragment.append(listItem);
      });

      this.tocList.append(fragment);
      this.toc.hidden = false;
      this.observeHeadings(headings);
    }

    observeHeadings(headings) {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top);

          if (!visible.length) return;

          this.headingLinks.forEach((link) => link.classList.remove('is-active'));
          const activeLink = this.headingLinks.get(visible[0].target);
          activeLink?.classList.add('is-active');
        },
        { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
      );

      headings.forEach((heading) => this.observer.observe(heading));
    }

    bindHelpfulButtons() {
      const buttons = [...this.root.querySelectorAll('[data-helpful-value]')];
      const message = this.root.querySelector('[data-helpful-message]');
      if (!buttons.length) return;

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.helpfulValue;
          buttons.forEach((candidate) => {
            const selected = candidate === button;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-pressed', String(selected));
          });

          if (message) {
            message.textContent = this.root.dataset.helpfulThanks || 'Thank you for your feedback.';
          }

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'db_support_article_feedback',
            article_handle: this.root.dataset.articleHandle || '',
            helpful: value
          });
        });
      });
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.supportArticleInitialized === 'true') return;
      root.dataset.supportArticleInitialized = 'true';
      new DBSupportArticle(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
